import { Injectable, signal } from "@angular/core";
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, Observable, finalize, map, of, shareReplay, tap, throwError } from "rxjs";
import { Router } from '@angular/router';

export type UserLookupOption = {
  _id: string;
  full_name?: string;
  email?: string;
  company_name?: string;
  role?: string;
  user_type?: string;
};

@Injectable({
  providedIn: 'root',
})
export class UserHttpService {
  // Legacy localStorage token keys; remove after cookie-only auth is fully verified.
  // private readonly accessTokenKey = 'apiSnfKey';
  // private readonly refreshTokenKey = 'apiSnfRefreshKey';

  constructor(
    public httpClient: HttpClient,
    private router: Router,
  ) { }

  public apiUrl: string = environment.apiUrl;

  currentUser = signal<any | null>(null);
  /** True until the initial cookie-backed /auth/me request has finished. */
  authLoading = signal(true);
  private currentUserRequest$: Observable<any> | null = null;


  login(payload: any, recaptchaToken: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/login`, payload, {
      headers: new HttpHeaders({ 'x-recaptcha-token': recaptchaToken }),
    });
  }

  getCurrentUser(): Observable<any> {
    const currentUser = this.currentUser();
    if (currentUser) {
      this.authLoading.set(false);
      return of(currentUser);
    }

    if (this.currentUserRequest$) {
      return this.currentUserRequest$;
    }

    this.currentUserRequest$ = this.httpClient.get(`${this.apiUrl}/auth/me`).pipe(
      map((res: any) => res?.user ?? null),
      tap((user) => {
        this.currentUser.set(user);
        this.authLoading.set(false);
      }),
      catchError((error) => {
        this.currentUser.set(null);
        this.authLoading.set(false);
        return throwError(() => error);
      }),
      finalize(() => {
        this.currentUserRequest$ = null;
      }),
      shareReplay(1),
    );

    return this.currentUserRequest$;
  }

  setCurrentUser(user: any | null): void {
    this.currentUser.set(user);
    this.authLoading.set(false);
  }

  getCsrfToken(): Observable<any> {
    return this.httpClient.get(`${this.apiUrl}/auth/csrf`);
  }

  forgetPassword(email: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/password-reset/request`, { email, from: 'dashboard' });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/password-reset/confirm`, { token, password });
  }

  oAuthLogin(provider: 'google', credential: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/oauth/${provider}`, { credential });
  }

  removeOAuth(provider: string): Observable<any> {
    return this.httpClient.delete(`${this.apiUrl}/auth/oauth/${provider}`);
  }

  verify2FALogin(userId: string, token: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-login`, { userId, token });
  }

  request2FARecovery(userId: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/recovery/request`, { userId });
  }

  verify2FARecovery(userId: string, otp: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/recovery/verify`, { userId, otp });
  }

  requestOtp(): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/otp`, {});
  }

  verifyOtp(otp: string): Observable<any> {
    return this.httpClient.patch(`${this.apiUrl}/auth/verify`, { otp });
  }

  getProfiles(page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
    return this.getProfilesFromEndpoint('profiles', page, perPage, searchBy, searchTerm);
  }

  getSoftDeletedProfiles(page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
    return this.getProfilesFromEndpoint('profiles/soft-deleted', page, perPage, searchBy, searchTerm);
  }

  getActiveProfiles(page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
    return this.getProfilesFromEndpoint('profiles/active', page, perPage, searchBy, searchTerm);
  }

  getAdminProfiles(page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
    return this.getProfilesFromEndpoint('profiles/admins', page, perPage, searchBy, searchTerm);
  }

  private getProfilesFromEndpoint(endpoint: string, page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
    let params = new HttpParams()
      .set('page', String(page ?? 1))
      .set('perPage', String(perPage ?? 10));

    if (searchBy) {
      params = params.set('searchBy', searchBy);
    }

    const trimmedSearchTerm = (searchTerm ?? '').trim();
    if (trimmedSearchTerm) {
      params = params.set('searchTerm', trimmedSearchTerm);
    }

    return this.httpClient.get(`${this.apiUrl}/auth/${endpoint}`, { params });
  }

  lookupProfiles(search: string, limit = 20): Observable<UserLookupOption[]> {
    const trimmedSearch = search.trim();
    if (trimmedSearch.length < 2) {
      return of([]);
    }

    const params = new HttpParams()
      .set('search', trimmedSearch)
      .set('limit', String(limit));

    return this.httpClient.get<{ profiles?: UserLookupOption[] }>(`${this.apiUrl}/auth/profiles/lookup`, { params }).pipe(
      map((res) => Array.isArray(res?.profiles) ? res.profiles : []),
    );
  }

  exportProfilesExcel(): Observable<Blob> {
    return this.httpClient.get(`${this.apiUrl}/auth/profiles/export/excel`, {
      responseType: 'blob',
    });
  }

  getProfile(id: string): Observable<any> {
    return this.httpClient.get(`${this.apiUrl}/auth/profile/${id}`);
  }

  getCustomerReports(id: string, page = 1, perPage = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('perPage', String(perPage));
    return this.httpClient.get(`${this.apiUrl}/auth/profile/${id}/reports`, { params });
  }

  createUserAsAdmin(payload: FormData): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/admin/users`, payload);
  }

  updateProfile(id: string, payload: any): Observable<any> {
    return this.httpClient.patch(`${this.apiUrl}/auth/profile/${id}`, payload);
  }

  deleteCompanyLogo(id: string): Observable<any> {
    return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}/company-logo`);
  }

  changePassword(id: string, payload: { current_password?: string; new_password: string; confirm_password: string }): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/reset-password-when-login/${id}`, payload);
  }

  resetClientPasswordAsSuperAdmin(id: string, payload: { new_password: string; confirm_password: string }): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/admin/users/${id}/password`, payload);
  }

  deleteProfile(id: string): Observable<any> {
    return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}`);
  }

  softDeleteProfile(id: string): Observable<any> {
    return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}/soft`);
  }

  enable2FA(): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-enable`, {});
  }

  verify2FASetup(token: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-verify-setup`, { token });
  }

  disable2FA(payload: { current_password?: string; token: string }): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-disable`, payload);
  }

  request2FABackupEmail(backup_email: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/backup-email/request`, { backup_email });
  }

  verify2FABackupEmail(otp: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/backup-email/verify`, { otp });
  }

  requestAdmin2FABackupEmail(userId: string, backup_email: string): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/2fa/admin/users/${userId}/backup-email/request`, { backup_email });
  }

  patchCurrentUser(
    patch: Partial<{
      full_name: string;
      email: string;
      user_type: string;
      role: string;
      verified: boolean;
      two_factor_enabled: boolean;
      two_factor_backup_email: string;
      two_factor_backup_email_verified: boolean;
      _id: string;
      auth_provider: string;
      oauth_provider: string | null;
    }>,
  ): void {
    const current = this.currentUser();
    if (!current) {
      return;
    }
    this.currentUser.set({ ...current, ...patch });
  }



  refreshToken(): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}/auth/refresh`, {});
  }


  // Legacy localStorage token helpers; remove after cookie-only auth is fully verified.
  // setTokens(accessToken: string, refreshToken?: string): void {
  //   localStorage.setItem(this.accessTokenKey, accessToken);
  //   if (refreshToken) {
  //     localStorage.setItem(this.refreshTokenKey, refreshToken);
  //   } else {
  //     localStorage.removeItem(this.refreshTokenKey);
  //   }
  //   this.restoreUser();
  // }
  //
  // setToken(token: string): void {
  //   this.setTokens(token);
  // }
  //
  // getToken(): string | null {
  //   return localStorage.getItem(this.accessTokenKey);
  // }
  //
  // getRefreshToken(): string | null {
  //   return localStorage.getItem(this.refreshTokenKey);
  // }

  isCurrentUserVerified(): boolean {
    const verified = this.currentUser()?.verified;
    return verified === true || verified === 'true' || verified === 1 || verified === '1';
  }

  refreshAccessToken(): Observable<void> {
    return this.refreshToken().pipe(
      map(() => undefined),
    );
  }

  logout(): void {
    this.currentUser.set(null);
    this.currentUserRequest$ = null;
    // Navigate immediately so a failed refresh cannot leave an expired session on a protected page.
    void this.router.navigate(['/login']);
    this.httpClient.post(`${this.apiUrl}/auth/logout`, {}).subscribe({
      next: () => this.currentUser.set(null),
      error: () => this.currentUser.set(null),
    });
  }

  // Legacy JWT restore; remove after cookie-only auth is fully verified.
  // restoreUser(): void {
  //   const token = this.getToken();
  //   if (!token) {
  //     this.currentUser.set(null);
  //     return;
  //   }
  //
  //   try {
  //     const payload: any = jwtDecode(token);
  //     this.currentUser.set(payload);
  //   } catch {
  //     this.logout();
  //   }
  // }
}
